import { useEffect, useRef, useState } from "react";
import { all_routes } from "../../router/all_routes";
import Toasts from "../../../core/common/toast";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  verifyLoginOtp,
  resendOtp,
} from "../../../core/data/redux/adminAuthSlice";
import { useNavigate } from "react-router";

import {
  maskEmail,
  maskPhoneNumber,
} from "../../../core/hooks/commonFunctions";
import CountdownWithResend from "../../../core/common/timer/timer";

const schema = Yup.object().shape({
  otp: Yup.string()
    .required("OTP is required")
    .length(6, "OTP must be 6 characters"),
});

const TwoStepVerification = () => {
  const dispatch = useDispatch();
  const employeeAuthState = useSelector(
    (state: any) => state.adminAuthState
  );
  const routes = all_routes;
  const navigation = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  // OTP input handling
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [otpArray, setOtpArray] = useState<string[]>(Array(6).fill(""));

  const handleOtpChange = (value: string, index: number) => {
    const alphanumeric = /^[a-zA-Z0-9]$/;

    if (value && alphanumeric.test(value)) {
      const upperCaseValue = value.toUpperCase();

      const newOtpArray = [...otpArray];
      newOtpArray[index] = upperCaseValue;
      setOtpArray(newOtpArray);

      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }

      const otpString = newOtpArray.join("");
      setValue("otp", otpString);
    } else if (value === "") {
      const newOtpArray = [...otpArray];
      newOtpArray[index] = "";
      setOtpArray(newOtpArray);
      setValue("otp", newOtpArray.join(""));
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    
    // Filter only alphanumeric characters and take first 6
    const alphanumeric = /^[a-zA-Z0-9]+$/;
    const filteredData = pastedData
      .split("")
      .filter((char) => alphanumeric.test(char))
      .slice(0, 6)
      .map((char) => char.toUpperCase());

    if (filteredData.length > 0) {
      const newOtpArray = [...otpArray];
      
      // Fill the OTP array with pasted data
      filteredData.forEach((char, i) => {
        if (i < 6) {
          newOtpArray[i] = char;
        }
      });

      setOtpArray(newOtpArray);
      const otpString = newOtpArray.join("");
      setValue("otp", otpString);

      // Focus on the next empty input or the last input
      const nextIndex = Math.min(filteredData.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const onSubmit = (data: any) => {
    const otpValue = data.otp;
    // Instantly wipe sensitive OTP from form state & inputs (Finding 12)
    setOtpArray(Array(6).fill(""));
    setValue("otp", "");

    dispatch(
      verifyLoginOtp({
        employee_id: employeeAuthState?.user?.data?.data?.employee_id,
        otp: otpValue,
        action: "ADMIN_LOGIN",
      })
    );
  };

  useEffect(() => {
    if (!employeeAuthState.action) {
      navigation(routes.login);
    } else if (
      employeeAuthState.loading === "fulfilled" &&
      employeeAuthState.action === "verify-login-otp"
    ) {
      navigation(routes.adminDashboard);
    }
  }, [employeeAuthState.loading]);

  const handleTimerExpire = () => {
    // Clear OTP input fields when timer expires
    setOtpArray(Array(6).fill(""));
    setValue("otp", "");
  };

  const handleResend = () => {
    // Clear OTP input fields when resending
    setOtpArray(Array(6).fill(""));
    setValue("otp", "");

    // Focus on first input field
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    dispatch(
      resendOtp({
        employee_id: employeeAuthState?.user?.data?.data?.employee_id,
      })
    );
  };

  return (
    <div className="container-fuild">
      <Toasts
        show={employeeAuthState.loading === "rejected"}
        message={employeeAuthState.error}
        title={"Login Failed"}
        type={"danger"}
      />
      <Toasts
        show={employeeAuthState.loading === "fulfilled"}
        message={employeeAuthState?.user?.data?.message}
        title={"Success"}
        type={"success"}
      />
      <div className="hero">
        <div className="sub-hero">
          <div className="img-logo">
            <img src="/assets/img/login/logo.png" alt="logoimage" />
          </div>
          <p className="text-white fs-3  fw-bold sub-title text-center">
            Two Step Verification
          </p>
          <div className="text-center">
            <p className="text-white">
              Please enter the OTP received to confirm your account ownership.
              <br /> A code has been sent to <br />
              <span className="text-white">
                {maskEmail(employeeAuthState?.user?.data?.data?.email)}
              </span>{" "}
              and{" "}
              <span className="text-white">
                {maskPhoneNumber(employeeAuthState?.user?.data?.data?.phone)}
              </span>
            </p>
          </div>

          {/* Form */}
          <div>
            <form
              action=""
              onSubmit={handleSubmit(onSubmit)}
              className="form-container"
            >
              <div className="text-center otp-input mt-5">
                <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
                  {otpArray.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength={1}
                      value={digit}
                      className="form-input-otp text-center"
                      onChange={(e) => handleOtpChange(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={handlePaste}
                      ref={(el) => (inputRefs.current[index] = el)}
                    />
                  ))}
                </div>
                {errors.otp && (
                  <small className="text-danger align-items-left">
                    {errors.otp.message}
                  </small>
                )}
                <CountdownWithResend
                  handleResendOtp={handleResend}
                  onTimerExpire={handleTimerExpire}
                  initialMinutes={0}
                  initialSeconds={30}
                />
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={employeeAuthState.loading === "pending"}
              >
                {employeeAuthState.loading === "pending"
                  ? "Verifying..."
                  : "Verify & Proceed"}
              </button>
              <p className="terms-text">
                By clicking on Sign In, you agree to our Terms of service and
                Privacy Policy
              </p>
            </form>
          </div>
        </div>
        <div className="right-img">
          <div className="ladies-img">
            <img src="/assets/img/login/ladies.png" alt="" />
          </div>
          <div className="metro-img">
            <img src="/assets/img/login/metroo.png" alt="" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoStepVerification;
